"use client";

import Toast from "@/common/toast.common";
import { ToastContext } from "@/context";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { useState, useEffect, useRef } from "react";
import { useRouter, usePathname } from "next/navigation";

export default function Providers({ children }) {
  const router = useRouter();
  const pathname = usePathname();
  const [openToast, setOpenToast] = useState(false);
  const [toastConfig, setToastConfig] = useState({
    message: "",
    type: "success",
    autoHideDuration: 3000,
  });
  
  const isHandlingAuthError = useRef(false);

  const closeToast = () => {
    setOpenToast(false);
  };

  const showToast = (customToastConfig) => {
    setOpenToast(true);
    setToastConfig(customToastConfig);
  };

  const getLocale = () => {
    const pathParts = pathname.split('/').filter(Boolean);
    const locale = pathParts[0] || 'en'; 
    return locale;
  };

  const getLoginPath = () => {
    const locale = getLocale();
    return `/${locale}/login`;
  };

  const handleApiError = (error) => {
    console.error("API Error:", error);
    
    const isAuthError = 
      error.response?.status === 401 ||
      error.status === 401 ||
      error.message?.includes("Invalid or expired token") ||
      error.message?.includes("Unauthorized");
    
    if (isAuthError && !isHandlingAuthError.current) {
      isHandlingAuthError.current = true;
      
      showToast({
        message: "Your session has expired. Please log in again.",
        type: "error",
        autoHideDuration: 3000,
      });
      
      localStorage.removeItem("token");
      
      setTimeout(() => {
        router.push(getLoginPath());
        setTimeout(() => {
          isHandlingAuthError.current = false;
        }, 5000);
      }, 100);
    }
  };

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        retry: (failureCount, error) => {
          if (
            error.response?.status === 401 ||
            error.status === 401 ||
            error.message?.includes("Invalid or expired token")
          ) {
            return false;
          }
          return failureCount < 1;
        },
        onError: handleApiError,
      },
      mutations: {
        onError: handleApiError,
      },
    },
  });

  useEffect(() => {
    const originalFetch = window.fetch;
    
    window.fetch = async (...args) => {
      try {
        const response = await originalFetch(...args);
        
        if (response.status === 401) {
          throw { response: { status: 401 } };
        }
        
        return response;
      } catch (error) {
        handleApiError(error);
        throw error;
      }
    };

    return () => {
      window.fetch = originalFetch;
    };
  }, [router, pathname]);

  return (
    <QueryClientProvider client={queryClient}>
      <ToastContext.Provider value={{ showToast }}>
        {children}
      </ToastContext.Provider>
      <Toast open={openToast} onClose={closeToast} {...toastConfig} />
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}